import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import {
  IEmployeeOverrideWithEmployee,
  ILearningBudgetSetupInTheList,
  ITeamOverrideWithTeam,
  LearningBudgetSetupStatus,
} from '@/types/learning-budget';
import { MaterialReactTable } from 'material-react-table';
import { useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  CompanyRow,
  EmployeeRow,
  TableRowVariant,
  TeamRow,
} from '../LearningBudgetSetupsTable.types';
import { CompanyLevelLearningBudgetModal } from '../modals/CompanyLevelLearningBudgetModal';
import { EditEmployeeLearningBudgetModal } from '../modals/EditEmployeeLearningBudgetModal';
import { EditTeamLearningBudgetModal } from '../modals/EditTeamLearningBudgetModal';
import {
  LearningBudgetSetMembersModal,
  SubmissionValues,
} from '../modals/LearningBudgetSetMembersModal';
import {
  LearningBudgetSetTeamsModal,
  TeamSubmissionValues,
} from '../modals/LearningBudgetSetTeamsModal';
import { EmployeeTeamOverridesTable } from './EmployeeTeamOverridesTable';
import { LearningBudgetSetupActions } from './LearningBudgetSetupActions';
import {
  createColumns,
  type TableRow,
} from './LearningBudgetSetupItemDetailTable.columns';

interface LearningBudgetSetupItemDetailTableProps {
  setup: ILearningBudgetSetupInTheList;
  onSaveSetup: (data: ILearningBudgetSetupInTheList) => Promise<void>;
}

export function LearningBudgetSetupItemDetailTable({
  setup,
  onSaveSetup,
}: LearningBudgetSetupItemDetailTableProps) {
  const [isEditingCompanyLB, setIsEditingCompanyLB] = useState(false);
  const [employeeOverrideToEdit, setEmployeeOverrideToEdit] = useState<
    IEmployeeOverrideWithEmployee | undefined
  >();
  const [teamOverrideToEdit, setTeamOverrideToEdit] = useState<
    ITeamOverrideWithTeam | undefined
  >();

  const defaultTableOptions = getDefaultMRTOptions<TableRow>();
  const [isAddingEmployeeOverride, setIsAddingEmployeeOverride] =
    useState(false);
  const [isAddingTeamOverride, setIsAddingTeamOverride] = useState(false);

  const methods = useForm<ILearningBudgetSetupInTheList>({
    defaultValues: setup,
    disabled: setup.status !== LearningBudgetSetupStatus.Draft,
  });

  const { watch } = methods;

  const defaultBudget = watch('defaultBudget');

  const {
    append: appendEmployeeOverride,
    update: updateEmployeeOverride,
    remove: removeEmployeeOverride,
    fields: employeeOverrides,
  } = useFieldArray({
    control: methods.control,
    name: 'employeeOverrides',
  });

  const {
    append: appendTeamOverride,
    update: updateTeamOverride,
    remove: removeTeamOverride,
    fields: teamOverrides,
  } = useFieldArray({
    control: methods.control,
    name: 'teamOverrides',
  });

  const tableData = createTableData(
    {
      ...setup,
      employeeOverrides,
      teamOverrides,
      defaultBudget,
    },
    setup,
  );

  const handleAddEmployeeOverride = useCallback(
    (values?: SubmissionValues) => {
      if (values) {
        for (const employee of values?.employees ?? []) {
          if (employeeOverrides.some(e => e.employee._id === employee._id)) {
            updateEmployeeOverride(
              employeeOverrides.findIndex(e => e.employee._id === employee._id),
              {
                employee: {
                  _id: employee._id,
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                  team: employee.team,
                },
                budget: values.budget,
              },
            );
          } else {
            appendEmployeeOverride({
              employee: {
                _id: employee._id,
                firstName: employee.firstName,
                lastName: employee.lastName,
                team: employee.team,
              },
              budget: values.budget,
            });
          }
        }
      }
      setIsAddingEmployeeOverride(false);
    },
    [employeeOverrides, updateEmployeeOverride, appendEmployeeOverride],
  );

  const handleAddTeamOverride = useCallback(
    (values?: TeamSubmissionValues) => {
      if (values) {
        for (const team of values?.teams ?? []) {
          if (teamOverrides.some(t => t.team._id === team._id)) {
            updateTeamOverride(
              teamOverrides.findIndex(t => t.team._id === team._id),
              {
                team: {
                  _id: team._id,
                  name: team.name,
                },
                budget: values.budget,
              },
            );
          } else {
            appendTeamOverride({
              team: {
                _id: team._id,
                name: team.name,
              },
              budget: values.budget,
            });
          }
        }
      }
      setIsAddingTeamOverride(false);
    },
    [appendTeamOverride, updateTeamOverride, teamOverrides],
  );

  const handleEdit = (row: TableRow) => {
    if (row.variant === TableRowVariant.Company) {
      setIsEditingCompanyLB(true);
    } else if (row.variant === TableRowVariant.Team) {
      setTeamOverrideToEdit({
        team: row.team,
        budget: row.budget,
      });
    } else if (row.variant === TableRowVariant.Employee) {
      setEmployeeOverrideToEdit({
        employee: row.employee,
        budget: row.budget,
      });
    }
  };

  const handleDelete = (row: TableRow) => {
    if (row.variant === TableRowVariant.Employee) {
      removeEmployeeOverride(
        employeeOverrides.findIndex(e => e.employee._id === row.employee._id),
      );
    } else if (row.variant === TableRowVariant.Team) {
      removeTeamOverride(
        teamOverrides.findIndex(t => t.team._id === row.team._id),
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <MaterialReactTable
        {...defaultTableOptions}
        columns={createColumns({
          onEdit: handleEdit,
          onDelete: handleDelete,
        })}
        data={tableData}
        enableRowActions={false}
        enableRowSelection={false}
        enableColumnFilters={false}
        enableSorting={false}
        enableTableHead={false}
        enablePagination={false}
        enableBottomToolbar={false}
        muiTablePaperProps={{
          ...defaultTableOptions.muiTablePaperProps,
          className: 'bg-light-gray',
        }}
        muiTableBodyCellProps={{
          className: '!bg-transparent text-primary',
        }}
        muiTableBodyRowProps={{
          className: '!bg-transparent',
        }}
        enableTopToolbar={true}
        muiDetailPanelProps={{
          classes: {
            root: 'p-0 bg-light-gray',
          },
        }}
        renderTopToolbar={() => (
          <LearningBudgetSetupActions
            onSetTeamLB={() => {
              setIsAddingTeamOverride(true);
            }}
            onSetMembersLB={() => setIsAddingEmployeeOverride(true)}
            onSaveSetup={async () => {
              await methods.handleSubmit(onSaveSetup)();
            }}
          />
        )}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 0,
            minSize: 0,
            maxSize: 0,
            header: '',
            Header: () => null,
            Cell: () => null,
          },
        }}
        renderDetailPanel={({ row }) => {
          if (row.original.variant !== TableRowVariant.Team) {
            return null;
          }

          const { employeeOverrides = [] } = row.original;

          if (!employeeOverrides.length) {
            return null;
          }

          return (
            <EmployeeTeamOverridesTable
              employeeOverrides={employeeOverrides}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          );
        }}
      />
      <LearningBudgetSetMembersModal
        open={isAddingEmployeeOverride}
        onClose={handleAddEmployeeOverride}
      />
      <LearningBudgetSetTeamsModal
        open={isAddingTeamOverride}
        onClose={handleAddTeamOverride}
      />
      <CompanyLevelLearningBudgetModal
        open={isEditingCompanyLB}
        onClose={async values => {
          if (values) {
            methods.setValue('defaultBudget.amount', values.amount.amount, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }
          setIsEditingCompanyLB(false);
        }}
        defaultValues={{
          startDate: new Date(setup.startDate),
          endDate: new Date(setup.endDate),
          amount: setup.defaultBudget,
        }}
        mode="edit"
      />
      <EditEmployeeLearningBudgetModal
        open={!!employeeOverrideToEdit}
        onClose={data => {
          if (data) {
            updateEmployeeOverride(
              employeeOverrides.findIndex(
                e => e.employee._id === data.employee._id,
              ),
              data,
            );
          }
          setEmployeeOverrideToEdit(undefined);
        }}
        employeeOverride={employeeOverrideToEdit}
      />
      <EditTeamLearningBudgetModal
        open={!!teamOverrideToEdit}
        onClose={data => {
          if (data) {
            updateTeamOverride(
              teamOverrides.findIndex(t => t.team._id === data.team._id),
              data,
            );
          }
          setTeamOverrideToEdit(undefined);
        }}
        teamOverride={teamOverrideToEdit}
      />
    </FormProvider>
  );
}

function createTableData(
  setup: ILearningBudgetSetupInTheList,
  defaultValues: ILearningBudgetSetupInTheList,
): TableRow[] {
  const companyRow = {
    variant: TableRowVariant.Company,
    company: {
      _id: setup.company,
    },
    budget: setup.defaultBudget,
    setupStatus: setup.status,
    isDirty: !isEqual(setup.defaultBudget, defaultValues.defaultBudget),
  } as CompanyRow;

  const teamRows = (setup.teamOverrides ?? []).map(({ team, budget }) => ({
    variant: TableRowVariant.Team,
    team: {
      _id: team._id,
      name: team.name,
    },
    setupStatus: setup.status,
    budget,
    isDirty: !isEqual(
      budget,
      defaultValues.teamOverrides?.find(t => t.team._id === team._id)?.budget,
    ),
    employeeOverrides: (setup.employeeOverrides ?? [])
      .filter(({ employee }) => employee.team === team._id)
      .map(({ employee, budget }) => ({
        variant: TableRowVariant.Employee,
        employee: {
          _id: employee._id,
          firstName: employee.firstName,
          lastName: employee.lastName,
          team: team._id,
        },
        budget,
        setupStatus: setup.status,
        isDirty: !isEqual(
          budget,
          defaultValues.employeeOverrides?.find(
            e => e.employee._id === employee._id,
          )?.budget,
        ),
      })),
  })) as TeamRow[];

  const individualEmployeeRows = (setup.employeeOverrides ?? [])
    .filter(
      ({ employee }) =>
        !employee.team || !teamRows.some(t => t.team._id === employee.team),
    )
    .map(({ employee, budget }) => ({
      variant: TableRowVariant.Employee,
      employee: {
        _id: employee._id,
        firstName: employee.firstName,
        lastName: employee.lastName,
      },
      setupStatus: setup.status,
      budget,
      isDirty: !isEqual(
        budget,
        defaultValues.employeeOverrides?.find(
          e => e.employee._id === employee._id,
        )?.budget,
      ),
    })) as EmployeeRow[];

  return [companyRow, ...teamRows, ...individualEmployeeRows];
}
